import { useCallback, useRef } from 'react';
import type { FilterTags, PriceFilterTags } from '@/components/features';
import { PRICE_RANGES } from '@/constants/priceFilterRanges';
import type { BoundingBox } from '@/types/BoundingBox.types';
import type { Product } from '@/types/Product.types';

interface ProductCacheStore {
  [frameId: number]: {
    boundingBoxes: BoundingBox[];
    focusedBoxIndex: number;
    boxesData: {
      [boxLabel: string]: {
        products: Product[];
        pages: number;
        filterTags: FilterTags[];
        priceFilter: PriceFilterTags[];
      };
    };
  };
}

interface UseProductCacheReturn {
  haveBoundingBoxesInCache: (frameIdx: number) => boolean;
  setBoundingBoxCache: (frameIdx: number, boxes: BoundingBox[]) => void;
  getBoundingBoxCache: (frameIdx: number) => BoundingBox[];
  haveProductsInCache: (frameIdx: number, boxLabel: string) => boolean;
  setProductsCache: (
    frameIdx: number,
    boxLabel: string,
    products: Product[]
  ) => void;
  getProductsCache: (frameIdx: number, boxLabel: string) => Product[];
  getPagesCountCache: (frameIdx: number, boxLabel: string) => number;
  setPagesCountCache: (
    frameIdx: number,
    boxLabel: string,
    pageNum: number
  ) => void;
  getFocusedBoxIndexCache: (frameIdx: number) => number;
  setFocusedBoxIndexCache: (frameIdx: number, focusedBoxIndex: number) => void;
  getPriceFiltersCache: (
    frameIdx: number,
    boxLabel: string
  ) => PriceFilterTags[];
  setPriceFiltersCache: (
    frameIdx: number,
    boxLabel: string,
    priceFilters: PriceFilterTags[]
  ) => void;
  deleteBoxDataFromCache: (frameIdx: number, boxLabel: string) => void;
  clearAllCache: () => void;
}

const useProductCache = (): UseProductCacheReturn => {
  const productCacheStore = useRef<ProductCacheStore>({});

  const haveBoundingBoxesInCache = useCallback((frameIdx: number): boolean => {
    if (
      productCacheStore.current[frameIdx] &&
      productCacheStore.current[frameIdx].boundingBoxes &&
      productCacheStore.current[frameIdx].boundingBoxes.length
    ) {
      return true;
    }
    return false;
  }, []);

  const setBoundingBoxCache = useCallback(
    (frameIdx: number, boxes: BoundingBox[]): void => {
      // Initialize the frame object if it doesn't exist
      if (!productCacheStore.current[frameIdx]) {
        productCacheStore.current[frameIdx] = {
          boundingBoxes: [],
          focusedBoxIndex: 0,
          boxesData: {},
        };
      }
      productCacheStore.current[frameIdx].boundingBoxes = boxes;
    },
    []
  );

  const getBoundingBoxCache = useCallback(
    (frameIdx: number): BoundingBox[] => {
      if (haveBoundingBoxesInCache(frameIdx))
        return productCacheStore.current[frameIdx].boundingBoxes;
      else return [];
    },
    [haveBoundingBoxesInCache]
  );

  const haveProductsInCache = useCallback(
    (frameIdx: number, boxLabel: string): boolean => {
      if (
        productCacheStore.current[frameIdx] &&
        productCacheStore.current[frameIdx].boxesData[boxLabel] &&
        productCacheStore.current[frameIdx].boxesData[boxLabel].products &&
        productCacheStore.current[frameIdx].boxesData[boxLabel].products.length
      ) {
        return true;
      }
      return false;
    },
    []
  );

  const setProductsCache = useCallback(
    (frameIdx: number, boxLabel: string, products: Product[]): void => {
      // Initialize the frame object if it doesn't exist
      if (!productCacheStore.current[frameIdx]) {
        productCacheStore.current[frameIdx] = {
          boundingBoxes: [],
          focusedBoxIndex: 0,
          boxesData: {},
        };
      }
      // Initialize the box data if it doesn't exist
      if (!productCacheStore.current[frameIdx].boxesData[boxLabel]) {
        productCacheStore.current[frameIdx].boxesData[boxLabel] = {
          products: [],
          pages: 0,
          filterTags: [],
          priceFilter: [],
        };
      }
      productCacheStore.current[frameIdx].boxesData[boxLabel].products =
        products;
    },
    []
  );

  const getProductsCache = useCallback(
    (frameIdx: number, boxLabel: string): Product[] => {
      if (haveProductsInCache(frameIdx, boxLabel))
        return productCacheStore.current[frameIdx].boxesData[boxLabel].products;
      return [];
    },
    [haveProductsInCache]
  );

  const getPagesCountCache = useCallback(
    (frameIdx: number, boxLabel: string): number => {
      if (
        productCacheStore.current[frameIdx] &&
        productCacheStore.current[frameIdx].boxesData[boxLabel] &&
        productCacheStore.current[frameIdx].boxesData[boxLabel].pages
      ) {
        return productCacheStore.current[frameIdx].boxesData[boxLabel].pages;
      }
      return 0;
    },
    []
  );

  const setPagesCountCache = useCallback(
    (frameIdx: number, boxLabel: string, pageNum: number): void => {
      // Initialize the frame object if it doesn't exist
      if (!productCacheStore.current[frameIdx]) {
        productCacheStore.current[frameIdx] = {
          boundingBoxes: [],
          focusedBoxIndex: 0,
          boxesData: {},
        };
      }
      // Initialize the box data if it doesn't exist
      if (!productCacheStore.current[frameIdx].boxesData[boxLabel]) {
        productCacheStore.current[frameIdx].boxesData[boxLabel] = {
          products: [],
          pages: 0,
          filterTags: [],
          priceFilter: [],
        };
      }
      productCacheStore.current[frameIdx].boxesData[boxLabel].pages = pageNum;
    },
    []
  );

  const deleteBoxDataFromCache = useCallback(
    (frameIdx: number, boxLabel: string): void => {
      if (productCacheStore.current[frameIdx]) {
        delete productCacheStore.current[frameIdx].boxesData[boxLabel];
      }
    },
    []
  );

  const getFocusedBoxIndexCache = useCallback(
    (frameIdx: number): number => {
      if (
        haveBoundingBoxesInCache(frameIdx) &&
        productCacheStore.current[frameIdx].focusedBoxIndex
      )
        return productCacheStore.current[frameIdx].focusedBoxIndex;
      return 0;
    },
    [haveBoundingBoxesInCache]
  );

  const setFocusedBoxIndexCache = useCallback(
    (frameIdx: number, focusedBoxIndex: number): void => {
      if (!productCacheStore.current[frameIdx]) {
        productCacheStore.current[frameIdx] = {
          boundingBoxes: [],
          focusedBoxIndex: 0,
          boxesData: {},
        };
      }
      productCacheStore.current[frameIdx].focusedBoxIndex = focusedBoxIndex;
    },
    []
  );

  const getPriceFiltersCache = useCallback(
    (frameIdx: number, boxLabel: string): PriceFilterTags[] => {
      if (
        productCacheStore.current[frameIdx] &&
        productCacheStore.current[frameIdx].boxesData[boxLabel] &&
        productCacheStore.current[frameIdx].boxesData[boxLabel].priceFilter
      ) {
        return productCacheStore.current[frameIdx].boxesData[boxLabel]
          .priceFilter;
      }
      return PRICE_RANGES;
    },
    []
  );

  const setPriceFiltersCache = useCallback(
    (
      frameIdx: number,
      boxLabel: string,
      priceFilters: PriceFilterTags[]
    ): void => {
      // Initialize the frame object if it doesn't exist
      if (!productCacheStore.current[frameIdx]) {
        productCacheStore.current[frameIdx] = {
          boundingBoxes: [],
          focusedBoxIndex: 0,
          boxesData: {},
        };
      }
      // Initialize the box data if it doesn't exist
      if (!productCacheStore.current[frameIdx].boxesData[boxLabel]) {
        productCacheStore.current[frameIdx].boxesData[boxLabel] = {
          products: [],
          pages: 0,
          filterTags: [],
          priceFilter: [],
        };
      }
      productCacheStore.current[frameIdx].boxesData[boxLabel].priceFilter =
        priceFilters;
    },
    []
  );

  const clearAllCache = useCallback(() => {
    productCacheStore.current = {};
  }, []);

  return {
    haveBoundingBoxesInCache,
    setBoundingBoxCache,
    getBoundingBoxCache,
    haveProductsInCache,
    setProductsCache,
    getProductsCache,
    getPagesCountCache,
    setPagesCountCache,
    getFocusedBoxIndexCache,
    setFocusedBoxIndexCache,
    getPriceFiltersCache,
    setPriceFiltersCache,
    deleteBoxDataFromCache,
    clearAllCache,
  };
};

export default useProductCache;
